"use client";

import {
  useEffect,
  useRef,
  useState,
  Suspense,
  PropsWithChildren,
  useCallback,
  RefObject,
  MutableRefObject,
} from "react";
import { ChatMessageBubble, Message } from "./ChatMessageBubble";
import "highlight.js/styles/gradient-dark.css";
import { Spinner } from "@chakra-ui/react";
import { apiBaseUrl } from "../utils/constants";
import { SignInModal } from "./SignInModal";
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { isObject } from "lodash";
import { IAssistant, IChatSession } from "layouts/DefaultLayout";
import { captureException } from "scripts/logging/error-report";

import { IFilters } from "../components/Filters/Filters.def";
import { joinWithSpace, sanitizeString } from "utils/helpers";
import { isListingPath } from "utils/routes";
import { ChatMessageInput } from "./ChatMessageInput";
import { IUser } from "services/UserContext";
import { State } from "types/react";
import {
  IChatMessageServiceResponse,
  ISendChatMessageResponse,
} from "types/services";
import { IUserLocationInfo } from "scripts/hooks/useUserLocationInfo";
import { useQueryFlags, consumeServerInput } from "scripts/hooks/useQueryFlags";
import { useShareContext } from "services/ShareContext";
import { headers } from "services/api-client/configs";
import { Operation } from "operations/operation";
import { useSignInHandlers } from "scripts/hooks/useSignInHandlers";
import { trackEvent, matomoUserId } from "services/api-client/matomoTracker";
import { getStoredModel, reasoningEffortPayload } from "utils/models";
import { useWindowResize } from "scripts/hooks/useWindowResize";
import { eSearchType, SearchFilterOperation } from "operations/search-filter-operation";

export interface IChatWindowProps {
  title: string | JSX.Element | null;
  user?: IUser;
  assistant: IAssistant;
  assistantParams?: Record<string, unknown>;
  onNewAssistantSession?(date: string, session: IChatSession): void;
  Template?: (props: ChatWindow.ICustomTemplateProps) => JSX.Element;
  isSetMinHeight?: boolean;
  location?: IUserLocationInfo;
}

// Query params carrying a prompt the SPA auto-sends on load. They are stripped
// from the URL once sent, so a reload or a client-side navigation cannot replay
// them.
const ALREADY_SENT_PROMPT_QUERY_PARAMS = ['question', 'listing_filter', 'q'] as const;

const removeAlreadySentPromptQueryParams = (search: string) => {
  const searchParams = new URLSearchParams(search);
  ALREADY_SENT_PROMPT_QUERY_PARAMS.forEach((key) => searchParams.delete(key));
  const newSearch = searchParams.toString();
  return newSearch;
};

/**
 * Whether stripping would actually remove something.
 *
 * Note this asks the params, rather than comparing the rewritten query string
 * against the current one. The two are NOT equivalent: URLSearchParams
 * normalizes on the way out — `?embed` becomes `?embed=`, `%20` becomes `+` —
 * so a string comparison reports a difference for URLs that carry none of these
 * keys at all, and the navigation it guards would still fire.
 */
export const hasAlreadySentPromptQueryParams = (search: string) => {
  const searchParams = new URLSearchParams(search);
  return ALREADY_SENT_PROMPT_QUERY_PARAMS.some((key) => searchParams.has(key));
};

// here tempMessages variable using to persist messages across component remounts
let tempMessages: Array<any> = [];
export function ChatWindow(props: IChatWindowProps) {
  const {
    title,
    assistant,
    assistantParams,
    Template = DefaultTemplate,
    onNewAssistantSession,
    isSetMinHeight = true,
    location,
  } = props;
  const { hideUser, search, messageId, initialQuestion, hasListingFilter, listingFilters, displayErrorMsg
    // userId, rating, violation
  } = useQueryFlags();
  const { session: sessionUrl, token, harid } = useParams();
  const { pathname } = useLocation();
  const [messages, setMessages] = useState<Array<Message>>(tempMessages);
  const [input, setInput] = useState("");
  const [searchPrompt, setSearchPrompt] = useState("");
  const [operation, setOperation] = useState<Operation>();
  const [isMessagesFetching, setIsMessagesFetching] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showLimitExceedError, setShowLimitExceedError] = useState(false);
  const [sessionId, setSessionId] = useState(sessionUrl);
  const navigate = useNavigate();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState<IFilters>({ For: "Sale" });
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { setStatusCode } = useShareContext();
  const loadedSessionsRef = useRef<Set<string>>(new Set());
  const [isMoveDownButtonShown, setIsMoveDownButtonShown] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const newMsgRef = useRef<HTMLDivElement | null>(null);
  const lastUserMessageIdRef = useRef<string | null>(null); // Track the ID of the last user message sent
  // Re-entrancy guard for sendMessage. A ref, not the isSending state: the
  // initialQuestion and listingFilters effects can fire in the same flush, and
  // both would read a stale `isSending === false` and send concurrently.
  const isSendingRef = useRef(false);
  // Content-keyed guard so a given listing_filter payload is auto-sent exactly
  // once. Survives StrictMode's dev mount→unmount→remount (same fiber, refs
  // persist), which would otherwise fire sendMessage twice; a genuinely new
  // filter payload has a different key and still processes.
  const processedListingFilterRef = useRef<string | null>(null);

  // Discarding the tempMessages carry-over is a mount-time concern: a genuine
  // route change remounts ChatWindow (usePreservedRouteKey bumps its key), and
  // the in-mount navigations we perform ourselves all set sessionUrl, so this
  // must happen at most once per mount.
  //
  // The ref matters. StrictMode double-invokes effects in dev, and this one has
  // no cleanup, so it ran twice — while the listingFilters effect below skipped
  // its second pass via processedListingFilterRef. Without the guard, pass 2
  // wiped the optimistic messages pass 1 had just appended and nothing ever
  // re-added them, leaving the send with an empty list to reconcile against.
  const hasResetCarryOverRef = useRef(false);
  useEffect(() => {
    if (hasResetCarryOverRef.current) return;
    // Only reset if there's no sessionUrl (no session ID in the URL)
    if (!sessionUrl && (pathname === "/" || isListingPath(pathname))) {
      hasResetCarryOverRef.current = true;
      tempMessages = [];
      setMessages([]);
      // Clear the loaded sessions cache when resetting
      loadedSessionsRef.current.clear();
    }
  }, [sessionUrl, pathname]);

  const sendMessage = async (message?: string, operation?: Operation) => {
    const userMessage = message ?? input;
    const sanitizedMessage = sanitizeString(userMessage);

    if (isSendingRef.current || userMessage === "") return;
    isSendingRef.current = true;

    setInput("");

    const newUserMessageId = Math.random().toString();
    const newLoadingMessageId = Math.random().toString();

    // Track this message ID so we can attach ref to it for scrolling
    lastUserMessageIdRef.current = newUserMessageId;

    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];
      const isErrorMessage =
        lastMessage?.role === "error" && lastMessage?.error;
      const messages = [
        ...(!!isErrorMessage ? prevMessages.slice(0, -2) : prevMessages),
        {
          id: newUserMessageId,
          content: userMessage,
          role: "user",
          message_id: 0, // will be updated after the response is received
          metadata: operation?.getUserMetadata(),
          operation: operation?.toJson(),
        },
        {
          id: newLoadingMessageId,
          content: "",
          //metadata: userMetadata,
          message_id: 0,
          role: "loading",
          operation: operation?.toJson(),
        },
      ];
      tempMessages = messages;
      return messages as Message[];
    });

    setIsSending(true);
    try {
      const baseUrl = apiBaseUrl + "/api/v1/chatbot/chat";
      const url = sessionId ? `${baseUrl}/${sessionId}` : baseUrl;
      // Both are staff-only overrides -- the backend ignores them for everyone
      // else -- and both are read at send time, so a change in the header picker
      // applies to the next message without the chat having to re-render.
      const model = getStoredModel();
      const requestPayload = {
        query: btoa(sanitizedMessage),
        assistant: assistant.type,
        assistantId: assistant.id,
        params: assistantParams,
        sessionIdentifier: sessionId ?? "",
        model,
        ...reasoningEffortPayload(model),
        // metadata now travels with each message via operation?.getUserMetadata()
        ...(location ? { location } : {}),
        operation: operation?.toJson(),
      };

      const response = await fetchApi(url, requestPayload);
      if (!response.ok) {
        setStatusCode(response.status);
        if (response.status === 429) setShowLimitExceedError(true)
      } else {
        trackEvent("AI Mode / User Send Message", { uid: matomoUserId(props.user) });
        // Only touch history when one of those params is actually present. An
        // ordinary typed message carries none of them, and navigating to the URL
        // we are already on still re-renders every location consumer.
        if (hasAlreadySentPromptQueryParams(search)) {
          const newSearch = removeAlreadySentPromptQueryParams(search);
          navigate(`${pathname}${newSearch ? '?' + newSearch : ''}`, { replace: true });
        }
        setStatusCode(200);
      }

      const result: ISendChatMessageResponse = await response.json();

      if (!result || 'code' in result || (!response.ok && ![429, 404].includes(response.status))) {
        const error = 'detail' in result ? result.detail : null;
        throw new Error(error ?? "Oops! Something went wrong on our side. Please try again in a moment.");
      }

      if (response.status !== 200 && response.status !== 201) {
        // Drop only this exchange's optimistic pair, so the loading spinner
        // stops without discarding the rest of the conversation.
        setMessages((prev) => {
          const messages = prev.filter(
            (m) => m.id !== newUserMessageId && m.id !== newLoadingMessageId
          );
          tempMessages = messages;
          return messages;
        });
        return
      };

      const {
        answer: content,
        chat_title,
        artifacts,
        message_id,
        session_id,
        user_message: serverUserMessage,
      } = result;
      const storeSessionId = session_id;

      if (storeSessionId) {
        setSessionId(storeSessionId);
        // The messages we just received are the source of truth for this
        // session — mark it as loaded so the [sessionUrl] effect's guard
        // suppresses the otherwise-redundant /messages fetch that would fire
        // when navigate() updates sessionUrl below.
        loadedSessionsRef.current.add(storeSessionId);
        if (!sessionId) {
          onNewAssistantSession?.("Today", {
            ...(harid ? { harid } : {}),
            session_id: storeSessionId,
            title: chat_title,
          });
          const newSearch = removeAlreadySentPromptQueryParams(search);
          navigate(`./${storeSessionId}${newSearch ? '?' + newSearch : ''}`, {
            replace: true,
            state: { noRerender: true },
          });
        }
      }
      setMessages((prev) => {
        // 1️⃣ prepare the assistant reply
        const assistantMsg: Message = {
          id: Math.random().toString(),
          content,
          metadata: artifacts,
          message_id: message_id.assistant,
          role: "assistant",
        };

        // 2️⃣ drop this exchange's loading placeholder and avoid accidental
        // duplicates. The optimistic user message is exempt from the dedupe: it
        // still carries message_id 0, which would collide with a falsy
        // message_id.assistant.
        const base = prev.filter(
          (m) =>
            m.id !== newLoadingMessageId &&
            (m.id === newUserMessageId ||
              m.message_id !== assistantMsg.message_id)
        );

        // 3️⃣ locate our own optimistic user message by id rather than by
        // position. If it is gone the list was reset mid-flight (route change,
        // failed sibling request), so there is nothing to reconcile and the
        // reply is dropped instead of resurrecting a stale exchange.
        const userIndex = base.findIndex((m) => m.id === newUserMessageId);
        if (userIndex === -1) return prev;

        // Overwrite the optimistic user message with the server's authoritative
        // copy (content + metadata). Both carry the server-generated search_id, so
        // replacing them together keeps the rendered HTML tag matched to its
        // metadata; a tampered client message/metadata is discarded here.
        const userMsg: Message = {
          ...base[userIndex],
          message_id: message_id.user,
          ...(serverUserMessage
            ? {
              content: serverUserMessage.content,
              metadata: serverUserMessage.metadata,
            }
            : {}),
        };

        const messages = [
          ...base.slice(0, userIndex),
          userMsg,
          assistantMsg,
          ...base.slice(userIndex + 1),
        ];
        tempMessages = messages;

        return messages;
      });

      // ✅ Removed anchorToBottom() - scroll-to-top will handle it
    } catch (error: any) {
      captureException(error);
      const errorMessage = (
        isObject(error) && "message" in error
          ? error.message
          : error || "Failed to load chat messages. Please try again later."
      ) as string;
      const isNotValidJsonError = error instanceof SyntaxError && error.message.includes('JSON');
      console.log("Error while chatting:", errorMessage ?? "Error while fetching chat history");
      const userMessage = message ?? input;

      setMessages((previousMessages) => {
        const messages =
          previousMessages.map((message) =>
            message.id === newLoadingMessageId
              ? { ...message, role: "error", error: isNotValidJsonError ? "Oops! Something went wrong on our side. Please try again in a moment." : errorMessage }
              : message
          );
        tempMessages = messages;
        return messages as Message[];
      });
      setSearchPrompt(userMessage);
      setOperation(operation);
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
    }
  };

  /**
   * Handle initialQuestion: Only for NEW conversations (no session/token, 0 messages)
   */
  useEffect(() => {
    if (!initialQuestion || sessionUrl || token || messages.length > 0) return;
    // Clear any server-injected question so it can't re-send on a later
    // client-side navigation to another new-chat route.
    consumeServerInput();
    sendMessage(initialQuestion);
  }, [initialQuestion, sessionUrl, token]);

  /**
   * Handle listingFilters: For BOTH new AND existing conversations
   * NOTE: Removed messages.length from dependencies to prevent re-triggering
   */
  useEffect(() => {
    if (!hasListingFilter || !listingFilters) return;

    // Skip if this exact payload was already auto-sent (StrictMode double-invoke
    // or an unrelated re-render that kept the same filters).
    const filterKey = JSON.stringify(listingFilters);
    if (processedListingFilterRef.current === filterKey) return;
    processedListingFilterRef.current = filterKey;

    const processFilters = async () => {
      try {
        // const transformedQueryStringFilters = await transformQueryStringFiltersObjectToFilters(listingFilters);
        // const uiFilters: IFilters = transformApiFilters(transformedQueryStringFilters);
        // const selectedFilters = { For: "Sale", ...uiFilters };
        // console.log({ transformedQueryStringFilters, listingFilters, uiFilters });

        // if there is sessionId or sessionUrl, it means it's an existing conversation so set message count to 1 otherwise 0
        //const currentMessageCount = sessionUrl || sessionId ? 1 : 0;
        // const questionAndFilters = createFilterQuestionAndSelectedFilters(
        //   allTransformedFilters,
        //   currentMessageCount
        // );

        const type = sessionUrl || sessionId ? eSearchType.Filter : eSearchType.Search;
        const operation = SearchFilterOperation.fromNative("search_listings", listingFilters, type);
        await sendMessage(operation.getUserMessage(), operation);
      } catch (error) {
        captureException(error);
        console.error('Error processing listing filters:', error);
      }
    };

    processFilters();
  }, [hasListingFilter, listingFilters]);


  useEffect(() => {
    setSessionId(sessionUrl);
    // Skip loading if listing filters are present
    if (listingFilters) return;

    // An in-flight send owns the message list: it has already appended its
    // optimistic pair and will reconcile it against the server's reply. Loading
    // here would race that and overwrite the result with a snapshot taken before
    // the exchange was persisted. This effect re-runs mid-send because
    // sendMessage's own navigate() strips the already-sent query params, which
    // changes `search` — a `/:session?listing_filter=...` load is skipped on
    // mount, so nothing has marked the session loaded yet and the guard below
    // would otherwise let the fetch through.
    if (isSendingRef.current) return;

    // Prevent unnecessary API calls if we already have messages for this session
    // but still allow loading for new sessions
    const currentSessionKey = sessionUrl || token || 'default';
    const hasMessagesForCurrentSession = messages.length > 0 && loadedSessionsRef.current.has(currentSessionKey);

    if (hasMessagesForCurrentSession) return;

    if (sessionUrl || token) {
      loadChatMessages(sessionUrl);
    }
  }, [sessionUrl, search]);

  const fetchApi = async (url: string, data: any) => {
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
      credentials: "include",
    });
    return response;
  };

  const loadChatMessages = async (session?: string) => {
    session = session || sessionId;
    try {
      setIsMessagesFetching(true);

      let url = "";
      let sessionKey = "";
      if (session) {
        url = `/${session}`;
        sessionKey = session;
      } else if (token) {
        url = `/shared/${token}?hide_user=${hideUser ? "1" : "0"}${messageId ? `&message_id=${messageId}` : ""}${displayErrorMsg ? `&display_error_msg=1` : ""}`;
        sessionKey = token;
        // url = `/shared/${token}?hide_user=${hideUser ? "1" : "0"}${userId ? `&user_id=${userId}` : ""}${rating ? `&rating=${rating}` : ""}${violation ? `&violation=${violation}` : ""}`;
      } else {
        throw new Error("No session or token provided");
      }

      const response = await fetch(
        `${apiBaseUrl}/api/v1/chatbot/messages${url}`,
        { credentials: "include", headers }
      );

      if (!response.ok) {
        setStatusCode(response.status);
      } else {
        setStatusCode(200);
      }

      const result: IChatMessageServiceResponse = await response.json();

      if (!result || 'detail' in result || (!response.ok && ![429, 404].includes(response.status))) {
        throw new Error(result?.detail ?? "Oops! Something went wrong on our side. Please try again in a moment.");
      }

      const chatMessages = result.session_chat_messages.sort(
        (a: any, b: any) => a.timestamp - b.timestamp
      );
      const messages: Message[] = [];
      for (const item of chatMessages) {
        const role = item.type === "ai" || item.type === "ai-home" ? "assistant" : "user";
        const id = Math.random().toString();
        if (role !== "assistant") {
          messages.push({
            id,
            content: item.content,
            role,
            message_id: item.message_id,
            metadata: item.artifacts,
            rating: item.rating,
            timestamp: item.timestamp,
            type: item.type,
          });
          continue;
        }

        const { content, artifacts, message_id, rating } = item;

        const message = {
          id,
          content,
          metadata: artifacts,
          message_id,
          role,
          rating,
          timestamp: item.timestamp,
          type: item.type,
        } as const satisfies Message;
        messages.push(message);
      }

      tempMessages = messages;
      setMessages(messages);

      // Mark this session as loaded to prevent unnecessary reloads
      if (sessionKey) {
        loadedSessionsRef.current.add(sessionKey);
      }
    } catch (error: any) {
      captureException(error);

      const errorMessage = (
        isObject(error) && "message" in error
          ? error.message
          : error || "Failed to load chat messages. Please try again later."
      ) as string;

      console.error("Error while fetching chat messages:", errorMessage ?? "Error while fetching chat history");

      setMessages((previousMessages) => {
        const messages = previousMessages.map((message) =>
          message.role === "loading"
            ? { ...message, role: "error", error: errorMessage }
            : message
        )
        tempMessages = messages;
        return messages as Message[];
      }
      );
    } finally {
      setIsMessagesFetching(false);
    }
  };


  const scrollMessageToTop = useCallback((el: HTMLDivElement | null) => {
    if (!el || !contentRef.current) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const wrapper = contentRef.current;
        if (!wrapper) return;

        // Compute scroll offset relative to the scroll container, not the element's offsetParent
        const wrapperRect = wrapper.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const relativeTop = elRect.top - wrapperRect.top;
        const targetScrollTop = wrapper.scrollTop + relativeTop - 5;

        wrapper.scrollTo({ top: targetScrollTop, behavior: "smooth" });
      });
    });
  }, []);

  // When a new user message is added, automatically scroll to bring it into view
  useEffect(() => {
    if (newMsgRef.current) {
      scrollMessageToTop(newMsgRef.current);
      newMsgRef.current = null;
    }
    // Clear the tracked message ID after scrolling
    lastUserMessageIdRef.current = null;
  }, [messages, scrollMessageToTop]);

  const moveScrollDown = useCallback(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: "smooth",
      });

      // Hide the button with fade effect after scrolling
      setTimeout(() => {
        setIsMoveDownButtonShown(false);
      }, 500);
    }
  }, []);

  // Monitor scroll to show/hide scroll down button with debouncing for better performance
  useEffect(() => {
    const handleScroll = () => {
      // Clear any existing timeout to debounce the scroll event
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Debounce the scroll handler to avoid excessive state updates
      scrollTimeoutRef.current = setTimeout(() => {
        if (contentRef.current) {
          const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
          const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold

          if (isAtBottom) {
            setIsMoveDownButtonShown(false);
          } else if (messages.length > 3) {
            setIsMoveDownButtonShown(true);
          }
        }
      }, 100); // 100ms debounce delay
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        contentElement.removeEventListener('scroll', handleScroll);
        // Clean up timeout on unmount
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }
  }, [messages.length]);

  const sendInitialQuestion = async (question: string, operation?: Operation) => {
    await sendMessage(question, operation);
    !props.user && setInput(question);
  };

  // Ref-based "latest handler" pattern: keeps a stable callback identity
  // while always calling the most recent sendInitialQuestion closure,
  // avoiding stale state (input, isSending, sessionId, etc.).
  const sendInitialQuestionRef = useRef(sendInitialQuestion);
  sendInitialQuestionRef.current = sendInitialQuestion;

  const onNewMessage = useCallback(
    (question: string, operation?: Operation) => {
      sendInitialQuestionRef.current(question, operation);
    },
    []
  );

  const handleImageLoad = () => {
    setIsMessagesFetching(false);
  };

  const Loading = useCallback(
    () => (

      <div className="text-center border_radius--pudgy bg_color_cement_light p-4 ml-auto mr-auto" style={{ maxWidth: '450px' }}>
        <div className="d-flex align-items-center">
          <div className="spinner-border mr-4 color_har_blue_dark" style={{ width: '2rem', height: '2rem' }} role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <div className="font_size--medium color_auxiliary">Loading Chat Messages...</div>
        </div>
      </div>

    ),
    []
  );

  const Content = ContentSection({
    filterModalOpenState: [isFilterModalOpen, setIsFilterModalOpen],
    filtersState: [filters, setFilters],
    contentRef,
    isSetMinHeight,
    isMoveDownButtonShown,
    moveScrollDown,
  });

  const Title = TitleSection({ title });

  const InputBar = InputBarSection({
    user: props.user,
    showLimitExceedErrorState: [showLimitExceedError, setShowLimitExceedError],
    isSending,
    inputState: [input, setInput],
    sendMessage,
    handleImageLoad,
  });

  const Messages = MessagesSection({
    isMessagesFetching,
    isSending,
    messages,
    Loading,
    onNewMessage,
    searchPrompt,
    operation,
    newMsgRef,
    lastUserMessageIdRef,
  });

  // 3️⃣ When the component mounts with an existing session but no data yet, show a skeleton instead of Empty‑State.
  if (!isListingPath(pathname) && isMessagesFetching) {
    return <Loading />;
  }

  // Keep the empty state until we have actual messages (not just loading/sending states)
  const isEmptyState = !messages.length && !isMessagesFetching;

  return (
    <Template
      user={props.user}
      isEmpty={isEmptyState}
      Content={Content}
      Title={Title}
      Messages={Messages}
      InputBar={InputBar}
      onMessage={sendInitialQuestion}
      showLimitExceedError={showLimitExceedError}
    />
  );
}

interface ContentSectionProps {
  filterModalOpenState: State<boolean>;
  filtersState: State<IFilters>;
  contentRef: RefObject<HTMLDivElement>;
  isSetMinHeight?: boolean;
  isMoveDownButtonShown: boolean;
  moveScrollDown: () => void;
}

const ContentSection = defineTemplate(
  (
    input: ContentSectionProps,
    props: PropsWithChildren<{ className?: string }>
  ) => {
    const {
      contentRef,
      isMoveDownButtonShown,
      moveScrollDown,
    } = input;
    const { pathname } = useLocation();

    return (
      <div
        className={joinWithSpace("cpr_content", props.className)}
        ref={contentRef}
      >
        <div className="cpr_content_inner">
          <div className="cpr_content_bottom position-relative">
            {isListingPath(pathname) && (
              <img src="https://content.harstatic.com/media/icons/a_all/chat_ai_icon2.svg" className="ai-mode-logo" alt="AI Mode Icon" />
            )}
            <div
              className="w-full mb-2 d-flex flex-column gap-2"
            >
              {props.children}
            </div>
          </div>
        </div>
        <div
          className={`cp_goto_top ${isMoveDownButtonShown ? 'visible' : 'hidden'}`}
          onClick={moveScrollDown}
          style={{
            cursor: 'pointer',
            transition: 'opacity 0.3s ease-in-out',
            opacity: isMoveDownButtonShown ? 1 : 0,
            pointerEvents: isMoveDownButtonShown ? 'auto' : 'none'
          }}
        >
          <img src="https://content.harstatic.com/media/icons/accordian_arrow_down.svg" alt="Scroll down" />
        </div>
      </div>
    );
  }
);

interface ITitleSectionProps {
  title: string | JSX.Element | null;
}

const TitleSection = defineTemplate((props: ITitleSectionProps) => !props.title ? <></> : (
  <div className="titlesection">
    {props.title}
  </div>
));

interface MessagesSectionProps {
  isMessagesFetching: boolean;
  isSending: boolean;
  messages: Message[];
  onNewMessage: (message: string, operation?: Operation) => void;
  Loading: () => JSX.Element;
  searchPrompt: string;
  operation?: Operation;
  newMsgRef: MutableRefObject<HTMLDivElement | null>;
  lastUserMessageIdRef: MutableRefObject<string | null>;
}

const MessagesSection = defineTemplate((props: MessagesSectionProps) => {
  const { pathname } = useLocation();
  const { width } = useWindowResize();

  const {
    isMessagesFetching,
    isSending,
    messages,
    onNewMessage,
    Loading,
    searchPrompt,
    operation,
    newMsgRef,
    lastUserMessageIdRef
  } = props;

  if (isMessagesFetching) {
    return <Loading />;
  }

  if (!messages.length && !isSending) {
    return <></>;
  }

  const calculateMinHeight = () => {
    if (isListingPath(pathname)) {
      if (width < 768) {
        return "calc(-325px + 100vh)";
      }
      return "calc(-387px + 100vh)";
    }
    if (width < 768) {
      return "calc(-257px + 100vh)";
    }
    return "calc(-325px + 100vh)";
  }

  return (
    <>
      {messages.map((data, index) => {
        const isLastMessage = index === messages.length - 1;
        // Attach ref only to the user message that was just sent (tracked by lastUserMessageIdRef)
        const shouldAttachRef = data.id === lastUserMessageIdRef.current;

        return (
          <div
            key={data.id}
            ref={shouldAttachRef ? (el) => { newMsgRef.current = el; } : null}
            style={isLastMessage ? { minHeight: calculateMinHeight() } : undefined}
          >
            <ChatMessageBubble
              message={data}
              isMostRecent={index === 0}
              messageCompleted={!isMessagesFetching}
              onNewMessage={onNewMessage}
              searchPrompt={searchPrompt}
              operation={operation}
            />
          </div>
        );
      })}
    </>
  );
});
interface InputBarSectionProps {
  user?: IUser;
  showLimitExceedErrorState: State<boolean>;
  isSending: boolean;
  inputState: State<string>;
  sendMessage: (message?: string) => void;
  handleImageLoad: () => void;
}

const InputBarSection = defineTemplate((props: InputBarSectionProps) => {
  const {
    user,
    showLimitExceedErrorState,
    isSending,
    inputState,
    sendMessage,
    handleImageLoad,
  } = props;
  const [input, setInput] = inputState;
  const { handleCreateAccount } = useSignInHandlers(user?.user_id);
  const [showLimitExceedError, setShowLimitExceedError] =
    showLimitExceedErrorState;

  return (
    <>
      {showLimitExceedError && (
        <div className="input_bar">
          <div
            className={`alert alert-danger alert-dismissible`}
            role="alert"
            style={{ transition: "opacity 0.3s ease-in-out" }}
          >
            <br />
            <p>
              Anonymous users can try AI Mode 20 times only. Please{" "}
              <a
                href="#"
                className="font_weight--semi_bold text-decoration-underline"
                data-toggle="modal"
                data-target="#registerLogin"
                onClick={(e) => e.preventDefault()}
              >
                Sign In
              </a>{" "}
              to your HAR account or{" "}
              <a
                href="#"
                className="font_weight--semi_bold text-decoration-underline"
                onClick={handleCreateAccount}
              >
                Sign Up
              </a>{" "}
              if you don't have an account to continue using AI Mode.
            </p>
            <p>
              A HAR account provides you access to many tools and features to
              assist you in your real estate journey:
            </p>
            <ul style={{ marginLeft: "14px" }}>
              <li style={{ listStyle: "inside" }}>
                Save searches and receive notifications for new listings
              </li>
              <li style={{ listStyle: "inside" }}>
                Bookmark your favorite properties
              </li>
              <li style={{ listStyle: "inside" }}>
                View traffic reports for all listings
              </li>
            </ul>
            <button
              type="button"
              className="close"
              aria-label="Close"
              onClick={() => setShowLimitExceedError(false)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </div>
      )}

      <ChatMessageInput
        isSending={isSending}
        sendMessage={sendMessage}
        input={input}
        setInput={setInput}
        handleImageLoad={handleImageLoad}
        showLimitExceedError={showLimitExceedError}
      />

      <SignInModal />
    </>
  );
});

function ChatWindowPage(props: IChatWindowProps) {
  return (
    <Suspense fallback={<Spinner />}>
      <ChatWindow {...props} />
    </Suspense>
  );
}

function DefaultTemplate(props: ChatWindow.ICustomTemplateProps) {
  const { isEmpty, Content, Title, Messages, InputBar } = props;
  return (
    <>
      <Content>
        {isEmpty && <Title />}
        <Messages />
      </Content>
      <InputBar />
    </>
  );
}

export namespace ChatWindow {
  export interface ICustomTemplateProps {
    user?: IUser;
    isEmpty: boolean;
    Content: (props: PropsWithChildren<{ className?: string }>) => JSX.Element;
    Title: () => JSX.Element;
    Messages: () => JSX.Element;
    InputBar: () => JSX.Element;
    onMessage(message: string): void;
    showLimitExceedError?: boolean;
  }
}

function useTemplateSection<T extends object, P extends any[]>(
  input: T,
  callback: (values: T, ...args: P) => JSX.Element
) {
  const values = useRef<T>(input);
  Object.assign(values.current, input);
  return useCallback((...args: P) => callback(values.current, ...args), []);
}

function defineTemplate<T extends object, P extends any[]>(
  callback: (values: T, ...args: P) => JSX.Element
) {
  return function useTemplate(input: T) {
    return useTemplateSection(input, callback);
  };
}

export default ChatWindowPage;
